import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import MainLayout from './MainLayout';
import { 
  CircularProgress,
  Box
} from '@mui/material';
import { SortablePaginatedTable } from './common';
import { useAuth } from '../contexts/AuthContext';
import useRefreshOnFocus from '../hooks/useRefreshOnFocus';
import { formatDate, formatCurrency } from '../utils/dateUtils'; // Import date formatting utilities
import { 
  Avatar, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper,
  Typography
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Description as ContractsIcon,
  Assessment as AuditIcon,
  AccountBalance as TransactionsIcon,
  TrendingUp as ForecastIcon,
  People as AccountsIcon,
  Settings as SettingsIcon,
  Group as UsersIcon,
  Email as EmailIcon
} from '@mui/icons-material';
import '../styles/Dashboard.css';
import './AdminDashboard.css';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { StatCardSkeleton } from './LoadingSkeleton';

// Define column configurations for the tables
const contractRenewalColumns = [
  { id: 'external_id', label: 'Contract ID', sortable: true },
  { id: 'contract_name', label: 'Contract Name', sortable: true },
  { 
    id: 'amount', 
    label: 'Amount', 
    sortable: true,
    align: 'right',
    render: (row) => formatCurrency(row.amount)
  },
  { 
    id: 'current_term_start_date', 
    label: 'Current Term Start', 
    sortable: true,
    render: (row) => formatDate(row.current_term_start_date)
  },
  { 
    id: 'current_term_end_date', 
    label: 'Current Term End', 
    sortable: true,
    render: (row) => formatDate(row.current_term_end_date)
  },
  { 
    id: 'next_term_renewal_date', 
    label: 'Next Term Renewal', 
    sortable: true,
    render: (row) => formatDate(row.next_term_renewal_date)
  },
  { 
    id: 'next_term_start_date', 
    label: 'Next Term Start', 
    sortable: true,
    render: (row) => formatDate(row.next_term_start_date)
  },
  { 
    id: 'next_term_end_date', 
    label: 'Next Term End', 
    sortable: true,
    render: (row) => formatDate(row.next_term_end_date)
  }
];

const overdueInvoicesColumns = [
  { id: 'customer_name', label: 'Customer', sortable: true },
  { id: 'business_unit_name', label: 'Business Unit', sortable: true },
  { id: 'doc_number', label: 'Invoice #', sortable: true },
  { 
    id: 'invoice_date', 
    label: 'Invoice Date', 
    sortable: true,
    render: (row) => formatDate(row.invoice_date)
  },
  { 
    id: 'due_date', 
    label: 'Due Date', 
    sortable: true,
    render: (row) => formatDate(row.due_date)
  },
  { 
    id: 'days_overdue', 
    label: 'Days Overdue', 
    sortable: true,
    align: 'right',
    getValue: (row) => parseInt(row.days_overdue, 10) || 0
  },
  { 
    id: 'balance', 
    label: 'Balance', 
    sortable: true,
    align: 'right',
    render: (row) => formatCurrency(row.balance)
  }
];

export const AdminDashboard = () => {
  const { user, signOut } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cardLoading, setCardLoading] = useState({
    contracts: true,
    mrr: true,
    points: true,
    lastMonthPoints: true,
    renewals: true,
    invoices: true
  });
  const [sectionErrors, setSectionErrors] = useState({
    contracts: false,
    mrr: false,
    points: false,
    lastMonthPoints: false,
    renewals: false,
    invoices: false
  });
  const [dashboardData, setDashboardData] = useState({
    totalMRR: 0,
    totalActiveContracts: 0,
    contractTypes: [],
    totalPointsDelivered: 0,
    totalPointsLastMonth: 0,
    upcomingRenewals: [],
    overdueInvoices: []
  });

  // Create a dedicated function for fetching data that we can reuse
  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setCardLoading({
        contracts: true,
        mrr: true,
        points: true,
        lastMonthPoints: true,
        renewals: true,
        invoices: true
      });
      setSectionErrors({
        contracts: false,
        mrr: false,
        points: false,
        lastMonthPoints: false,
        renewals: false,
        invoices: false
      });
      
      // Use the API base URL that's actually working
      const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://pulse.marketersindemand.com';
      console.log('Using API base URL:', apiBaseUrl);
      
      // Fetch all data but handle each section's errors separately
      const newDashboardData = { ...dashboardData };
      const newSectionErrors = { ...sectionErrors };
      const newCardLoading = { ...cardLoading };
      
      try {
        // Fetch contracts count
        const contractsResponse = await fetch(`${apiBaseUrl}/api/contracts/active/count`).then(res => {
          if (!res.ok) throw new Error(`Failed to fetch contracts: ${res.status}`);
          return res.json();
        });
        newDashboardData.totalActiveContracts = contractsResponse.count || 0;
        newCardLoading.contracts = false;
        setCardLoading(prev => ({ ...prev, contracts: false }));
      } catch (error) {
        console.error('Contracts count fetch failed:', error);
        newSectionErrors.contracts = true;
        newCardLoading.contracts = false;
        setCardLoading(prev => ({ ...prev, contracts: false }));
      }
      
      try {
        // Fetch contract types 
        const typesResponse = await fetch(`${apiBaseUrl}/api/contracts/types`).then(res => {
          if (!res.ok) throw new Error(`Failed to fetch contract types: ${res.status}`);
          return res.json();
        });
        newDashboardData.contractTypes = typesResponse || [];
      } catch (error) {
        console.error('Contract types fetch failed:', error);
        newSectionErrors.contracts = true;
      }
      
      try {
        // Fetch MRR
        const mrrResponse = await fetch(`${apiBaseUrl}/api/contracts/mrr`).then(res => {
          if (!res.ok) throw new Error(`Failed to fetch MRR: ${res.status}`);
          return res.json();
        });
        newDashboardData.totalMRR = mrrResponse.total || 0;
        newCardLoading.mrr = false;
        setCardLoading(prev => ({ ...prev, mrr: false }));
      } catch (error) {
        console.error('MRR fetch failed:', error);
        newSectionErrors.mrr = true;
        newCardLoading.mrr = false;
        setCardLoading(prev => ({ ...prev, mrr: false }));
      }
      
      try {
        // Fetch points delivered
        const pointsResponse = await fetch(`${apiBaseUrl}/api/tasks/points-delivered/month`).then(res => {
          if (!res.ok) throw new Error(`Failed to fetch points: ${res.status}`);
          return res.json();
        });
        newDashboardData.totalPointsDelivered = pointsResponse.total || 0;
        newCardLoading.points = false;
        setCardLoading(prev => ({ ...prev, points: false }));
      } catch (error) {
        console.error('Points delivered fetch failed:', error);
        newSectionErrors.points = true;
        newCardLoading.points = false;
        setCardLoading(prev => ({ ...prev, points: false }));
      }
      
      try {
        // Fetch last month's points delivered
        const lastMonthPointsResponse = await fetch(`${apiBaseUrl}/api/tasks/points-delivered/last-month`).then(res => {
          if (!res.ok) throw new Error(`Failed to fetch last month points: ${res.status}`);
          return res.json();
        });
        newDashboardData.totalPointsLastMonth = lastMonthPointsResponse.total || 0;
        newCardLoading.lastMonthPoints = false;
        setCardLoading(prev => ({ ...prev, lastMonthPoints: false }));
      } catch (error) {
        console.error('Last month points fetch failed:', error);
        newSectionErrors.lastMonthPoints = true;
        newCardLoading.lastMonthPoints = false;
        setCardLoading(prev => ({ ...prev, lastMonthPoints: false }));
      }
      
      try {
        // Fetch contract renewals
        const renewalsResponse = await fetch(`${apiBaseUrl}/api/contracts/upcoming-renewals`).then(res => {
          if (!res.ok) throw new Error(`Failed to fetch renewals: ${res.status}`);
          return res.json();
        });
        console.log('Contract renewals data:', renewalsResponse);
        newDashboardData.upcomingRenewals = renewalsResponse || [];
      } catch (error) {
        console.error('Renewals fetch failed:', error);
        newSectionErrors.renewals = true;
      }
      
      try {
        // Fetch overdue invoices
        const invoicesResponse = await fetch(`${apiBaseUrl}/api/invoices/overdue`).then(res => {
          if (!res.ok) throw new Error(`Failed to fetch invoices: ${res.status}`);
          return res.json();
        });
        newDashboardData.overdueInvoices = invoicesResponse || [];
      } catch (error) {
        console.error('Invoices fetch failed:', error);
        newSectionErrors.invoices = true;
      }
      
      // Update state with whatever data we were able to fetch
      setDashboardData(newDashboardData);
      setSectionErrors(newSectionErrors);
      
      // Check if all sections failed
      const allSectionsFailed = Object.values(newSectionErrors).every(value => value === true);
      if (allSectionsFailed) {
        setError('Failed to load all dashboard data. Please try again later.');
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      setError('Failed to load dashboard data. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Register for page visibility refresh
  const { isVisible, manualRefresh } = useRefreshOnFocus(fetchDashboardData, [], {
    enabled: false, // Disable automatic refresh
    minTimeBetweenRefreshes: 1800000 // 30 minutes
  });
  
  // Initial data fetch
  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const handleLogout = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Failed to log out:', error);
    }
  };

  // Colors for the pie chart
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];
  
  return (
    <MainLayout title="Dashboard">
      {loading ? (
        <div className="loading-container">
          <CircularProgress />
        </div>
      ) : error ? (
        <div className="error-container">
          <p className="error-message">{error}</p>
          <p className="error-details">
            Check the console for more details or contact your administrator.
          </p>
        </div>
      ) : (
        <div className="scrollable-content">
          <div className="stats-grid five-cards">
            {/* MRR Card */}
            {cardLoading.mrr ? (
              <StatCardSkeleton />
            ) : (
              <div className="stat-card">
                <div className="stat-label">Total MRR</div>
                {sectionErrors.mrr ? (
                  <div className="stat-error">Error loading data</div>
                ) : (
                  <div className="stat-value">{formatCurrency(dashboardData.totalMRR)}</div>
                )}
              </div>
            )}
            
            {/* Active Contracts Card */}
            {cardLoading.contracts ? (
              <StatCardSkeleton />
            ) : (
              <div className="stat-card">
                <div className="stat-label">Total Active Contracts</div>
                {sectionErrors.contracts ? (
                  <div className="stat-error">Error loading data</div>
                ) : (
                  <div className="stat-value">{dashboardData.totalActiveContracts}</div>
                )}
              </div>
            )}
            
            {/* Contract Types Pie Chart Card */}
            {cardLoading.contracts ? (
              <StatCardSkeleton />
            ) : (
              <div className="stat-card">
                <div className="stat-label">Projects vs Recurring</div>
                {sectionErrors.contracts ? (
                  <div className="stat-error">Error loading data</div>
                ) : (
                  <div className="stat-chart">
                    <ResponsiveContainer width="100%" height={100}>
                      <PieChart>
                        <Pie
                          data={dashboardData.contractTypes}
                          cx="50%"
                          cy="50%"
                          outerRadius={40}
                          fill="#8884d8"
                          dataKey="count"
                          nameKey="contract_type"
                          label={false}
                          labelLine={false}
                          labelStyle={{ fontSize: '10px' }}
                          isAnimationActive={false}
                        >
                          {dashboardData.contractTypes.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value, name) => {
                          // Capitalize first letter for tooltip
                          const formattedName = name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
                          return [value, formattedName];
                        }}/>
                      </PieChart>
                    </ResponsiveContainer>
                    
                    {/* Add legend below chart for better readability */}
                    <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', marginTop: '8px' }}>
                      {dashboardData.contractTypes.map((entry, index) => (
                        <div key={`legend-${index}`} style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          marginRight: '10px',
                          fontSize: '12px'
                        }}>
                          <div style={{ 
                            width: '10px', 
                            height: '10px', 
                            backgroundColor: COLORS[index % COLORS.length],
                            marginRight: '5px'
                          }}></div>
                          {entry.contract_type.charAt(0).toUpperCase() + entry.contract_type.slice(1).toLowerCase()}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* Points Delivered This Month Card */}
            {cardLoading.points ? (
              <StatCardSkeleton />
            ) : (
              <div className="stat-card">
                <div className="stat-label">Points Delivered This Month</div>
                {sectionErrors.points ? (
                  <div className="stat-error">Error loading data</div>
                ) : (
                  <div className="stat-value">{Math.round(dashboardData.totalPointsDelivered)}</div>
                )}
              </div>
            )}
            
            {/* Points Delivered Last Month Card */}
            {cardLoading.lastMonthPoints ? (
              <StatCardSkeleton />
            ) : (
              <div className="stat-card">
                <div className="stat-label">Points Delivered Last Month</div>
                {sectionErrors.lastMonthPoints ? (
                  <div className="stat-error">Error loading data</div>
                ) : (
                  <div className="stat-value">{Math.round(dashboardData.totalPointsLastMonth)}</div>
                )}
              </div>
            )}
          </div>
          
          <div className="tables-container">
            {/* Upcoming Contract Renewals Table */}
            <div className="table-section">
              <h2 className="table-title">Upcoming Contract Renewals</h2>
              {sectionErrors.renewals ? (
                <div className="table-error-container">
                  <p className="table-error">Failed to load contract renewals data</p>
                </div>
              ) : dashboardData.upcomingRenewals.length === 0 ? (
                <div className="table-empty-container">
                  <p className="table-empty">No upcoming contract renewals</p>
                </div>
              ) : (
                <>
                {/* Debugging info - will help identify if we have renewals that aren't being displayed */}
                {console.log(`Raw renewals data before filtering: ${dashboardData.upcomingRenewals.length} items`)}
                {console.log('Sample renewal data:', dashboardData.upcomingRenewals.length > 0 ? 
                  JSON.stringify(dashboardData.upcomingRenewals[0]) : 'none')}
                <SortablePaginatedTable
                  data={dashboardData.upcomingRenewals.filter(renewal => {
                    // Only show active contracts
                    if (renewal.contract_status !== 'active') {
                      return false;
                    }
                    
                    // More permissive filtering - use next_term_renewal_date OR current_term_renewal_date
                    if (!renewal.next_term_renewal_date && !renewal.current_term_renewal_date) {
                      return false;
                    }
                    
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    
                    // Try next_term_renewal_date first, fall back to current_term_renewal_date
                    const renewalDate = renewal.next_term_renewal_date 
                      ? new Date(renewal.next_term_renewal_date) 
                      : new Date(renewal.current_term_renewal_date);
                    
                    return renewalDate >= today;
                  })}
                  columns={contractRenewalColumns}
                  rowsPerPage={5}
                  emptyMessage="No upcoming contract renewals"
                  tableContainerProps={{ className: "enhanced-table" }}
                  isContractDetailTable={false}
                />
                </>
              )}
            </div>
            
            {/* Overdue Invoices Table */}
            <div className="table-section">
              <h2 className="table-title">Overdue Invoices</h2>
              {sectionErrors.invoices ? (
                <div className="table-error-container">
                  <p className="table-error">Failed to load invoice data</p>
                </div>
              ) : dashboardData.overdueInvoices.length === 0 ? (
                <div className="table-empty-container">
                  <p className="table-empty">No overdue invoices</p>
                </div>
              ) : (
                <SortablePaginatedTable
                  data={dashboardData.overdueInvoices}
                  columns={overdueInvoicesColumns}
                  rowsPerPage={5}
                  emptyMessage="No overdue invoices"
                  tableContainerProps={{ className: "enhanced-table" }}
                  isContractDetailTable={false}
                  tableProps={{
                    sx: {
                      '& .MuiTableRow-root': {
                        '&.overdue-severe': {
                          backgroundColor: 'rgba(244, 67, 54, 0.1)',
                        },
                        '&.overdue-row': {
                          backgroundColor: 'rgba(255, 152, 0, 0.05)',
                        }
                      }
                    }
                  }}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
}; 